import React from "react";
import { useSelector } from "react-redux";
import { LinearProgress, Box, Typography, CircularProgress } from "../legacy-ui/materialCompat";
import { selectIsLoading, selectLoadingMessage } from "../../redux/Slices/loading.slice";
import HomePageSkeleton from "./Skeleton/HomePageSkeleton";
import DetailPageSkeleton from "./Skeleton/DetailPageSkeleton";
import SearchPageSkeleton from "./Skeleton/SearchPageSkeleton";
import ProfilePageSkeleton from "./Skeleton/ProfilePageSkeleton";
import CategoryPageSkeleton from "./Skeleton/CategoryPageSkeleton";
import GenreChildPageSkeleton from "./Skeleton/GenreChildPageSkeleton";
import ShowcasePageSkeleton from "./Skeleton/ShowcasePageSkeleton";

/**
 * ContentLoader - A loading indicator for the content area
 * This component shows a skeleton or loading state while keeping the sidebar visible
 *
 * @param {React.ReactNode} children - Content to render when not loading
 * @param {boolean} localLoading - Local loading state
 * @param {string} skeletonType - Type of skeleton to show: 'home', 'detail', 'search', 'profile', 'category', 'genre', or null for spinner
 */
const ContentLoader = ({ children, localLoading = false, skeletonType = null }) => {
    const globalLoading = useSelector(selectIsLoading);
    const loadingMessage = useSelector(selectLoadingMessage);

    // Show loading if either global or local loading is true
    const isLoading = globalLoading || localLoading;

    if (isLoading) {
        // If skeleton type is specified, show the appropriate skeleton
        if (skeletonType) {
            return renderSkeleton(skeletonType);
        }

        // Otherwise show the default spinner
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: "100vh",
                    background: "#121212",
                    position: "relative",
                }}
            >
                {/* Top progress bar */}
                <LinearProgress
                    color="error"
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        backgroundColor: "#212121",
                    }}
                />

                {/* Center loading spinner */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                    }}
                >
                    <CircularProgress
                        size={48}
                        sx={{
                            color: "#B41C20",
                        }}
                    />
                    {loadingMessage && (
                        <Typography
                            sx={{
                                color: "#BFBFBF",
                                fontSize: "18px",
                                fontWeight: 500,
                                marginTop: 2,
                            }}
                        >
                            {loadingMessage}
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    }

    return <>{children}</>;
};

/**
 * Render the appropriate skeleton based on type
 */
function renderSkeleton(type) {
    switch (type) {
        case "home":
            return <HomePageSkeleton showHero={true} />;
        case "detail":
            return <DetailPageSkeleton showRelated={true} showArtists={true} />;
        case "search":
            return <SearchPageSkeleton showFilters={true} rows={3} cols={4} />;
        case "profile":
            return <ProfilePageSkeleton showHistory={true} showFavorites={true} showMenu={true} />;
        case "category":
            return <CategoryPageSkeleton showCategories={true} showGenres={true} />;
        case "genre":
            return <GenreChildPageSkeleton isParent={false} rows={3} cols={5} />;
        case "genreParent":
            return <GenreChildPageSkeleton isParent={true} rows={2} cols={5} />;
        case "showcase":
            return <ShowcasePageSkeleton showHeader={false} rowCount={3} />;
        case "anavatan":
            return <ShowcasePageSkeleton showHeader={true} rowCount={3} />;
        case "contentGrid":
            return <ShowcasePageSkeleton showHeader={false} rowCount={3} />;
        default:
            // Fallback to showcase skeleton for unknown types
            return <ShowcasePageSkeleton showHeader={false} rowCount={3} />;
    }
}

export default ContentLoader;
